/**
 * One shared capture compute for the whole form region (08-PRV §5.1). The capture chips
 * (03-CAP) and the schedule preview (08-PRV) both read the SAME `useCapturePreview`
 * result, so the definition is computed once — a single debounced request, one result
 * identity that the ephemeral session keys its "cleared on recompute" behavior off.
 *
 * It also carries the coverage drill-in: the capture coverage line bumps
 * `viewUnscheduledNonce`, which the preview watches to expand its Unscheduled group and
 * scroll into view.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { CaptureResult } from 'types/dps-capture';
import { useCapturePreview } from '../use-capture-preview';
import { buildCaptureDefinition } from './utils';
import type { DevelopmentPaceScenarioFormData } from './types';

interface CapturePreviewContextValue {
  result: CaptureResult | null;
  loading: boolean;
  error: Error | null;
  /** Re-runs the current capture compute (recovers a timed-out draft preview). */
  retry: () => void;
  /** Bumped when the coverage line's "View unscheduled" is followed. */
  viewUnscheduledNonce: number;
  requestViewUnscheduled: () => void;
}

const CapturePreviewContext = createContext<CapturePreviewContextValue | null>(
  null
);

interface CapturePreviewProviderProps {
  values: DevelopmentPaceScenarioFormData;
  children: ReactNode;
}

/**
 * Provides the single capture result + the coverage drill-in signal to the capture
 * chips and the schedule preview.
 *
 * @param {CapturePreviewProviderProps} props - The current form values and children.
 * @return {JSX.Element} The provider.
 */
export function CapturePreviewProvider({
  values,
  children,
}: CapturePreviewProviderProps): JSX.Element {
  const definition = useMemo(() => buildCaptureDefinition(values), [values]);
  const { result, loading, error, retry } = useCapturePreview(definition);
  const [viewUnscheduledNonce, setViewUnscheduledNonce] = useState(0);

  const requestViewUnscheduled = useCallback(
    () => setViewUnscheduledNonce((nonce) => nonce + 1),
    []
  );

  const value = useMemo(
    () => ({
      result,
      loading,
      error,
      retry,
      viewUnscheduledNonce,
      requestViewUnscheduled,
    }),
    [
      result,
      loading,
      error,
      retry,
      viewUnscheduledNonce,
      requestViewUnscheduled,
    ]
  );

  return (
    <CapturePreviewContext.Provider value={value}>
      {children}
    </CapturePreviewContext.Provider>
  );
}

/**
 * Reads the shared capture-preview context.
 *
 * @return {CapturePreviewContextValue} The shared result + drill-in signal.
 * @throws {Error} When used outside a {@link CapturePreviewProvider}.
 */
export function useCapturePreviewContext(): CapturePreviewContextValue {
  const context = useContext(CapturePreviewContext);
  if (context === null) {
    throw new Error(
      'useCapturePreviewContext must be used within a CapturePreviewProvider'
    );
  }
  return context;
}
